import React from 'react';
import { Layout } from '../../components/layout/Layout';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';

const TermsPage = () => {
    return (
        <Layout>
            {/* Hero */}
            <section className="bg-gradient-to-br from-primary-900 via-primary-800 to-primary-900 text-white py-12">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Link 
                        to="/" 
                        className="inline-flex items-center gap-2 text-primary-200 hover:text-white mb-6 transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Home
                    </Link>
                    <div className="flex items-center gap-3 mb-4">
                        <FileText className="w-8 h-8" />
                        <h1 className="text-3xl sm:text-4xl font-bold">Terms & Conditions</h1>
                    </div>
                    <p className="text-primary-200">Last updated: 16th January, 2026</p>
                </div>
            </section>

            {/* Content */}
            <section className="py-12 bg-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="prose prose-lg max-w-none prose-headings:text-neutral-900 prose-p:text-neutral-700">
                        <p className="lead text-lg text-neutral-600 mb-8">
                            These Terms & Conditions govern your use of RecruitNearMe.com. By accessing or using our services, you agree to these terms.
                        </p>

                        <h2 className="text-2xl font-semibold mt-10 mb-4 text-neutral-900">1. About RecruitNearMe</h2>
                        <p>RecruitNearMe provides:</p>
                        <ul className="list-disc pl-6 space-y-2 text-neutral-700">
                            <li>A directory of recruitment agencies</li>
                            <li>CV submission and recruiter contact tools</li>
                            <li>Human-assisted multi-apply services</li>
                            <li>Interview preparation and interview-day support</li>
                        </ul>
                        <p>We act as a facilitator, not an employer or recruiter.</p>

                        <h2 className="text-2xl font-semibold mt-10 mb-4 text-neutral-900">2. Eligibility</h2>
                        <p>You must:</p>
                        <ul className="list-disc pl-6 space-y-2 text-neutral-700">
                            <li>Be at least 18 years old</li>
                            <li>Provide accurate and truthful information</li>
                            <li>Use the platform for lawful job-seeking purposes only</li>
                        </ul>

                        <h2 className="text-2xl font-semibold mt-10 mb-4 text-neutral-900">3. User Responsibilities</h2>
                        <p>You agree:</p>
                        <ul className="list-disc pl-6 space-y-2 text-neutral-700">
                            <li>Not to submit false, misleading, or harmful content</li>
                            <li>Not to misuse recruiter contact information</li>
                            <li>Not to attempt to bypass payment systems or platform controls</li>
                            <li>Not to upload malicious files or engage in abusive behaviour</li>
                        </ul>

                        <h2 className="text-2xl font-semibold mt-10 mb-4 text-neutral-900">4. Recruiter Interactions</h2>
                        <ul className="list-disc pl-6 space-y-2 text-neutral-700">
                            <li>RecruitNearMe does not guarantee job offers, interviews, or responses</li>
                            <li>Hiring decisions are solely made by recruiters or employers</li>
                            <li>We are not responsible for recruiter actions, delays, or outcomes</li>
                        </ul>

                        <h2 className="text-2xl font-semibold mt-10 mb-4 text-neutral-900">5. Payments & Subscriptions</h2>
                        <ul className="list-disc pl-6 space-y-2 text-neutral-700">
                            <li>Some services require payment (subscriptions or one-time fees)</li>
                            <li>Prices are clearly displayed before purchase</li>
                            <li>Payments are processed securely via third-party providers</li>
                            <li>Subscriptions can be cancelled at any time unless stated otherwise</li>
                            <li>Refunds are discretionary and assessed on a case-by-case basis</li>
                        </ul>

                        <h2 className="text-2xl font-semibold mt-10 mb-4 text-neutral-900">6. Multi-Apply Service</h2>
                        <ul className="list-disc pl-6 space-y-2 text-neutral-700">
                            <li>Applications are submitted based on information you provide</li>
                            <li>You are responsible for ensuring CV accuracy</li>
                            <li>RecruitNearMe is not liable for rejected or unsuccessful applications</li>
                        </ul>

                        <h2 className="text-2xl font-semibold mt-10 mb-4 text-neutral-900">7. Intellectual Property</h2>
                        <p>
                            All website content, branding, logos, and materials belong to RecruitNearMe unless otherwise stated. 
                            You may not copy, reproduce, or redistribute without permission.
                        </p>

                        <h2 className="text-2xl font-semibold mt-10 mb-4 text-neutral-900">8. Limitation of Liability</h2>
                        <p>To the fullest extent permitted by law:</p>
                        <ul className="list-disc pl-6 space-y-2 text-neutral-700">
                            <li>RecruitNearMe is not liable for indirect or consequential losses</li>
                            <li>We are not responsible for job outcomes or recruiter conduct</li>
                            <li>Services are provided on an "as is" basis</li>
                        </ul>

                        <h2 className="text-2xl font-semibold mt-10 mb-4 text-neutral-900">9. Termination</h2>
                        <p>We reserve the right to suspend or terminate accounts that:</p>
                        <ul className="list-disc pl-6 space-y-2 text-neutral-700">
                            <li>Breach these terms</li>
                            <li>Misuse the platform</li>
                            <li>Engage in fraudulent or abusive activity</li>
                        </ul>

                        <h2 className="text-2xl font-semibold mt-10 mb-4 text-neutral-900">10. Governing Law</h2>
                        <p>These terms are governed by the laws of England and Wales.</p>

                        <h2 className="text-2xl font-semibold mt-10 mb-4 text-neutral-900">11. Contact Information</h2>
                        <p>For support or legal enquiries:</p>
                        <p className="font-medium text-primary-600">support@recruitnearme.com</p>
                    </div>
                </div>
            </section>
        </Layout>
    );
};

export default TermsPage;
